// Detalle de usuario (admin/franquiciado/docente) — muestra datos básicos +
// contexto relevante según el rol del usuario visualizado:
//  • Estudiante: docente acompañante, evaluaciones (CEP/CAD)
//  • Docente: lista de estudiantes asignados
//  • Franquiciado/Admin: franquicia + último login
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, Mail, Phone, ShieldCheck, GraduationCap, BookHeart, Building2,
  Users2, Calendar, Layers, AlertCircle, CheckCircle2,
} from "lucide-react";

const ROLE_META = {
  admin: { label: "Administrador", color: "#1F2547", icon: ShieldCheck },
  franquiciado: { label: "Franquiciado", color: "#F5B82E", icon: Building2 },
  docente: { label: "Docente", color: "#2C49A0", icon: GraduationCap },
  estudiante: { label: "Estudiante", color: "#D63384", icon: BookHeart },
};

export default function UserDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user: me } = useAuth();
  const [user, setUser] = useState(null);
  const [students, setStudents] = useState([]);
  const [teacherDoc, setTeacherDoc] = useState(null);
  const [franchise, setFranchise] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true); setErr("");
      try {
        const { data } = await api.get(`/users/${id}`);
        if (cancel) return;
        setUser(data);

        // Cargar contexto según el rol del usuario
        if (data.role === "docente") {
          // Estudiantes asignados a este docente
          try {
            const { data: list } = await api.get("/users", { params: { role: "estudiante" } });
            setStudents((list || []).filter((u) => u.teacher_id === data.id));
          } catch { /* puede fallar si el visor no tiene permisos */ }
        }

        if (data.role === "estudiante" && data.teacher_id) {
          try {
            const { data: t } = await api.get(`/users/${data.teacher_id}`);
            setTeacherDoc(t);
          } catch { /* no autorizado */ }
        }

        if (data.franchise_id) {
          try {
            const { data: f } = await api.get(`/franchises/${data.franchise_id}`);
            setFranchise(f);
          } catch { /* franquicia desconocida o sin permiso */ }
        }

        if (data.role === "estudiante") {
          try {
            const { data: asg } = await api.get("/questionnaire-assignments", { params: { student_id: data.id } });
            setAssignments(asg || []);
          } catch { /* docente/estudiante puede no estar en scope */ }
        }
      } catch (e) {
        const detail = e?.response?.data?.detail;
        setErr(typeof detail === "string" ? detail : (detail?.message || "No fue posible cargar el usuario"));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [id]);

  const RoleIcon = user ? (ROLE_META[user.role]?.icon || ShieldCheck) : ShieldCheck;
  const roleColor = user ? (ROLE_META[user.role]?.color || "#A43790") : "#A43790";
  const roleLabel = user ? (ROLE_META[user.role]?.label || user.role) : "";

  const backHref = useMemo(() => {
    if (me?.role === "admin") return "/admin/usuarios";
    if (me?.role === "franquiciado") return "/franquiciado/usuarios";
    if (me?.role === "docente") return "/docente/mis-estudiantes";
    return "/";
  }, [me?.role]);

  if (loading) {
    return <div className="jasq-card p-10 text-center text-sm text-[#6E7591]" data-testid="user-detail-loading">Cargando usuario…</div>;
  }
  if (err) {
    return (
      <div className="jasq-card p-8 text-center" data-testid="user-detail-error">
        <AlertCircle className="text-[#A52258] mx-auto mb-2" />
        <p className="text-sm font-bold text-[#1F2547]">{err}</p>
        <button onClick={() => nav(backHref)} className="jasq-btn-ghost mt-4 inline-flex items-center gap-2" data-testid="user-detail-back">
          <ArrowLeft size={14} /> Volver
        </button>
      </div>
    );
  }
  if (!user) return null;

  return (
    <div className="space-y-6" data-testid="user-detail-page">
      <button onClick={() => nav(backHref)} className="text-sm font-bold text-[#6E7591] hover:text-[#1F2547] inline-flex items-center gap-1.5" data-testid="user-detail-back">
        <ArrowLeft size={14} /> Volver
      </button>

      {/* Encabezado */}
      <div className="jasq-card p-6 flex items-center gap-5">
        <img src={user.avatar} alt="" className="w-20 h-20 rounded-2xl bg-white border-2 border-[#E1E5F0] object-cover shrink-0" data-testid="user-detail-avatar" />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] tracking-wider uppercase font-extrabold text-[#A43790] flex items-center gap-2">
            <RoleIcon size={12} style={{ color: roleColor }} /> {roleLabel}
          </p>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#1F2547] truncate" data-testid="user-detail-name">{user.name}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-[#4C5478]">
            <span className="inline-flex items-center gap-1.5"><Mail size={12} className="text-[#6E7591]" /> {user.email}</span>
            {user.phone && <span className="inline-flex items-center gap-1.5"><Phone size={12} className="text-[#6E7591]" /> {user.phone}</span>}
            {franchise && (
              <span className="inline-flex items-center gap-1.5">
                <Building2 size={12} className="text-[#6E7591]" />
                {franchise.is_headquarters && <span className="text-[#F5B82E]">★</span>}
                <b className="text-[#1F2547]">{franchise.name}</b>
              </span>
            )}
          </div>
        </div>
        <span
          data-testid="user-detail-status"
          className={`jasq-pill text-xs font-bold ${(user.status || "active") === "active" ? "bg-[#E7F4E9] text-[#2A7A36]" : "bg-[#FFE9F0] text-[#A52258]"}`}>
          {(user.status || "active") === "active" ? "Activo" : "Suspendido"}
        </span>
      </div>

      {/* Datos generales */}
      <div className="grid md:grid-cols-2 gap-4">
        <InfoCard title="Información básica">
          <KV k="ID" v={<span className="font-mono">{user.id}</span>} />
          <KV k="Rol" v={<span className="capitalize">{user.role}</span>} />
          {user.created_at && <KV k="Creado" v={new Date(user.created_at).toLocaleString("es-ES")} />}
          {user.last_login_at && <KV k="Último ingreso" v={new Date(user.last_login_at).toLocaleString("es-ES")} />}
          {user.must_change_password && (
            <p className="text-[11px] text-[#A52258] font-bold inline-flex items-center gap-1 mt-2">
              <AlertCircle size={11} /> Debe cambiar su contraseña en el próximo ingreso.
            </p>
          )}
        </InfoCard>

        {user.role === "estudiante" && (
          <InfoCard title="Datos académicos">
            <KV k="Edad" v={user.age ? `${user.age} años` : "—"} />
            <KV k="Curso" v={user.grade || "—"} />
            <KV k="Estado diagnóstico" v={user.qualitative_status || "Pendiente"} />
            {teacherDoc ? (
              <div className="mt-2 pt-2 border-t border-[#E1E5F0]">
                <p className="text-[11px] tracking-wider uppercase font-extrabold text-[#2C49A0]">Docente acompañante</p>
                <button
                  onClick={() => nav(`${me?.role === "admin" ? "/admin" : me?.role === "franquiciado" ? "/franquiciado" : "/docente"}/usuario/${teacherDoc.id}`)}
                  className="mt-1 flex items-center gap-2 p-2 -mx-2 rounded-lg hover:bg-[#F7F8FA] w-full text-left"
                  data-testid="user-detail-teacher-link"
                >
                  <img src={teacherDoc.avatar} alt="" className="w-9 h-9 rounded-full bg-white border-2 border-[#E1E5F0] object-cover" />
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-[#1F2547] truncate">{teacherDoc.name}</p>
                    <p className="text-[11px] text-[#6E7591] truncate">{teacherDoc.email}</p>
                  </div>
                </button>
              </div>
            ) : (
              <p className="text-[11px] text-[#A52258] font-bold inline-flex items-center gap-1 mt-2"><AlertCircle size={11} /> Sin docente asignado.</p>
            )}
          </InfoCard>
        )}

        {user.role === "docente" && (
          <InfoCard title={`Estudiantes asignados (${students.length})`} testid="user-detail-students-card">
            {students.length === 0 ? (
              <p className="text-xs text-[#6E7591]">Aún no acompaña a ningún estudiante.</p>
            ) : (
              <ul className="space-y-1">
                {students.slice(0, 10).map((s) => (
                  <li key={s.id}>
                    <button
                      onClick={() => nav(`${me?.role === "admin" ? "/admin" : me?.role === "franquiciado" ? "/franquiciado" : "/docente"}/usuario/${s.id}`)}
                      className="w-full flex items-center gap-2 p-2 -mx-2 rounded-lg hover:bg-[#F7F8FA] text-left"
                      data-testid={`user-detail-student-${s.id}`}
                    >
                      <img src={s.avatar} alt="" className="w-8 h-8 rounded-full bg-white border-2 border-[#E1E5F0] object-cover" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-[#1F2547] truncate">{s.name}</p>
                        <p className="text-[11px] text-[#6E7591] truncate">{s.grade || "—"} · {s.email}</p>
                      </div>
                      <span className="jasq-pill bg-[#F7F8FA] text-[#1F2547] text-[10px]">{s.qualitative_status || "Pendiente"}</span>
                    </button>
                  </li>
                ))}
                {students.length > 10 && (
                  <li className="text-[11px] text-[#6E7591] text-center pt-1">+{students.length - 10} más</li>
                )}
              </ul>
            )}
          </InfoCard>
        )}

        {(user.role === "admin" || user.role === "franquiciado") && (
          <InfoCard title="Acceso y franquicia">
            <KV k="Sede" v={franchise ? franchise.name : (user.franchise_id || "Global")} />
            {user.is_super_admin && <KV k="Super-admin" v="Sí" />}
            {user.custom_role_id && <KV k="Rol personalizado" v={<span className="font-mono">{user.custom_role_id}</span>} />}
          </InfoCard>
        )}
      </div>

      {/* Evaluaciones (sólo estudiantes) */}
      {user.role === "estudiante" && assignments.length > 0 && (
        <InfoCard title={`Cuestionarios asignados (${assignments.length})`} testid="user-detail-assignments-card">
          <ul className="divide-y divide-[#E1E5F0]">
            {assignments.map((a) => (
              <li key={a.id} className="py-2 flex items-center gap-3 text-sm">
                <Layers size={14} className="text-[#A43790]" />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-[#1F2547] truncate">{a.template_title || a.template_code}</p>
                  <p className="text-[11px] text-[#6E7591]">{a.template_code} · {a.kind || "cep"}</p>
                </div>
                <span className={`jasq-pill text-[10px] font-bold ${a.status === "completed" ? "bg-[#E7F4E9] text-[#2A7A36]" : a.status === "in_progress" ? "bg-[#FFF7DA] text-[#9C7A18]" : "bg-[#F7F8FA] text-[#6E7591]"}`}>
                  {a.status === "completed" ? "Completado" : a.status === "in_progress" ? "En curso" : "Pendiente"}
                </span>
                {a.due_date && (
                  <span className="text-[10px] text-[#6E7591] inline-flex items-center gap-1">
                    <Calendar size={10} /> {new Date(a.due_date).toLocaleDateString("es-ES")}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </InfoCard>
      )}
    </div>
  );
}

function InfoCard({ title, children, testid }) {
  return (
    <div className="jasq-card p-5" data-testid={testid}>
      <h3 className="font-display font-extrabold text-[#1F2547] mb-3">{title}</h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex items-baseline gap-3 text-sm">
      <span className="text-[11px] uppercase tracking-wider font-bold text-[#6E7591] min-w-[120px]">{k}</span>
      <span className="font-bold text-[#1F2547] flex-1 break-all">{v}</span>
    </div>
  );
}
